// Recover model-authored quotation formatting on native platforms, where raw
// HTML is text. Only a small presentational vocabulary is converted; no HTML,
// CSS, URLs, or event handlers are executed. Code fences remain source code.
const TAGS = new Set(['blockquote', 'strong', 'b', 'em', 'i', 'br', 'p']);
const escapeText = text => text.replace(/[\t\r\n ]+/g, ' ').replace(/\\/g, '\\\\')
  .replace(/([`*_{}\[\]()#+|~])/g, '\\$1')
  .replace(/</g, '&lt;').replace(/>/g, '&gt;');
const emphasis = (text, marker) => text.replace(/^(\s*)([\s\S]*?)(\s*)$/, (_, before, body, after) =>
  body ? `${before}${marker}${body}${marker}${after}` : text);

function format(node) {
  const body = node.text;
  if (node.tag === 'strong' || node.tag === 'b') return emphasis(body, '**');
  if (node.tag === 'em' || node.tag === 'i') return emphasis(body, '*');
  if (node.tag === 'p') return `\n\n${body.trim()}\n\n`;
  return `\n\n${body.trim().split('\n').map(line => `> ${line.trim()}`).join('\n')}\n\n`;
}

function readQuote(source, start, streaming) {
  const tokens = /<(\/?)([a-z][\w:-]*)\b(?:[^"'<>]|"[^"]*"|'[^']*')*>/gi;
  tokens.lastIndex = start;
  const stack = [];
  let cursor = start;
  let token;
  while ((token = tokens.exec(source))) {
    const tag = token[2].toLowerCase();
    if (!TAGS.has(tag) || (!stack.length && tag !== 'blockquote')) return null;
    if (stack.length) stack[stack.length - 1].text += escapeText(source.slice(cursor, token.index));
    else if (token.index !== start) return null;
    cursor = tokens.lastIndex;
    if (token[1]) {
      if (stack.at(-1)?.tag !== tag) return null;
      const rendered = format(stack.pop());
      if (!stack.length) return { end: cursor, markdown: rendered };
      stack[stack.length - 1].text += rendered;
    } else if (tag === 'br') {
      if (!stack.length) return null;
      stack[stack.length - 1].text += '  \n';
    } else {
      stack.push({ tag, text: '' });
    }
  }
  if (!streaming || !stack.length) return null;
  let tail = source.slice(cursor);
  // A token can end halfway through a tag. Keep its text, but wait to paint
  // that partial formatting tag until the next stream update completes it.
  tail = tail.replace(/<\/?([a-z]*)$/i, (partial, name) =>
    [...TAGS].some(tag => tag.startsWith(name.toLowerCase())) ? '' : partial);
  stack[stack.length - 1].text += escapeText(tail);
  while (stack.length > 1) {
    const rendered = format(stack.pop());
    stack[stack.length - 1].text += rendered;
  }
  return { end: source.length, markdown: format(stack.pop()) };
}

export function normalizeQuotedHtml(content, { isStreaming = false } = {}) {
  if (typeof content !== 'string' || !/<blockquote\b/i.test(content)
    || /^\uFEFF?\s*(?:<!doctype\s+html\b|<html(?:\s|>))/i.test(content)) return content;
  const codeSpans = [...content.matchAll(/(`+)([\s\S]*?)\1(?!`)/g)].map(match => [match.index, match.index + match[0].length]);
  let spanIndex = 0;
  let output = '', cursor = 0, position = 0, fence = null;
  for (const line of content.match(/[^\n]*(?:\n|$)/g) || []) {
    if (!line) continue;
    const start = position;
    position += line.length;
    if (start < cursor) continue;
    const marker = /^ {0,3}(`{3,}|~{3,})([^\n]*)/.exec(line);
    if (marker) {
      if (!fence) fence = marker[1];
      else if (marker[1][0] === fence[0] && marker[1].length >= fence.length && !marker[2].trim()) fence = null;
      continue;
    }
    if (fence || !/^ {0,3}<blockquote\b/i.test(line)) continue;
    const tagStart = start + line.indexOf('<');
    while (spanIndex < codeSpans.length && codeSpans[spanIndex][1] <= tagStart) spanIndex++;
    if (codeSpans[spanIndex]?.[0] <= tagStart) continue;
    const quote = readQuote(content, tagStart, isStreaming);
    if (!quote) continue;
    output += content.slice(cursor, start) + quote.markdown;
    cursor = quote.end;
  }
  return output + content.slice(cursor);
}
